import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { ScrollArea } from '../ui/scroll-area';
import { Button } from '../ui/button';
import { useLanguage } from '../../context/LanguageContext';
import api, { inspectionAPI } from '../../lib/api';
import { 
    Car, User, Calendar, Fuel, AlertTriangle, Camera,
    Wrench, Clock, MapPin, CheckCircle, XCircle, Loader2,
    ChevronLeft, ChevronRight, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

export default function VehicleSummaryModal({ isOpen, onClose, vehicle }) {
    const { language } = useLanguage();
    const [loading, setLoading] = useState(true);
    const [summaryData, setSummaryData] = useState(null);
    const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
    const [loadedPhotos, setLoadedPhotos] = useState({});
    const [loadingPhotos, setLoadingPhotos] = useState({});

    useEffect(() => {
        if (isOpen && vehicle) {
            // Reset photo states when opening for a new vehicle
            setLoadedPhotos({});
            setLoadingPhotos({});
            setSelectedPhotoIndex(0);
            fetchVehicleSummary();
        }
    }, [isOpen, vehicle]);

    const fetchVehicleSummary = async () => {
        setLoading(true);
        try {
            // Fetch last inspection with photos
            const inspectionsRes = await api.get(`/inspections?vehicle_id=${vehicle.id}&limit=1`);
            const lastInspection = inspectionsRes.data?.[0] || null;

            // Fetch damage alerts for this vehicle
            let damages = [];
            try {
                const damagesRes = await api.get(`/photo-comparison/alerts?vehicle_id=${vehicle.id}`);
                damages = damagesRes.data || [];
            } catch (e) {
                // Damage alerts might not exist
            }

            // Fetch maintenance records
            let maintenanceRecords = [];
            try {
                const maintenanceRes = await api.get(`/maintenance?vehicle_id=${vehicle.id}&limit=3`);
                maintenanceRecords = maintenanceRes.data || [];
            } catch (e) {
                // Maintenance might not exist
            }

            // Get driver info if assigned
            let lastDriver = null;
            if (lastInspection?.driver_id) {
                try {
                    const driverRes = await api.get(`/drivers/${lastInspection.driver_id}`);
                    lastDriver = driverRes.data;
                } catch (e) {
                    // Driver might not exist
                }
            } else if (vehicle.assigned_driver_id) {
                try {
                    const driverRes = await api.get(`/drivers/${vehicle.assigned_driver_id}`);
                    lastDriver = driverRes.data;
                } catch (e) {
                    // Driver might not exist
                }
            }

            setSummaryData({
                lastInspection,
                damages: damages.filter(d => d.vehicle_id === vehicle.id).slice(0, 5),
                maintenanceRecords,
                lastDriver,
                photos: lastInspection?.photos || [],
                checkoutPhotos: lastInspection?.checkout_photos || [],
                fuelLevel: lastInspection?.fuel_level || null
            });
        } catch (error) {
            console.error('Failed to fetch vehicle summary:', error);
            toast.error(language === 'el' ? 'Αποτυχία φόρτωσης σύνοψης' : 'Failed to load summary');
        } finally {
            setLoading(false);
        }
    };

    const fuelLevelConfig = {
        empty: { label: language === 'el' ? 'Άδειο' : 'Empty', color: 'text-red-500', bg: 'bg-red-100', percent: 0 },
        quarter: { label: '1/4', color: 'text-orange-500', bg: 'bg-orange-100', percent: 25 },
        half: { label: '1/2', color: 'text-yellow-500', bg: 'bg-yellow-100', percent: 50 },
        three_quarter: { label: '3/4', color: 'text-green-400', bg: 'bg-green-100', percent: 75 },
        full: { label: language === 'el' ? 'Γεμάτο' : 'Full', color: 'text-green-600', bg: 'bg-green-100', percent: 100 }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const allPhotos = [
        ...(summaryData?.photos || []).map(p => ({ 
            ...p, 
            type: 'checkin', 
            photoType: p.photo_type,
            inspectionId: summaryData?.lastInspection?.id
        })),
        ...(summaryData?.checkoutPhotos || []).map(p => ({ 
            ...p, 
            type: 'checkout', 
            photoType: p.type || p.photo_type,
            inspectionId: summaryData?.lastInspection?.id
        }))
    ];

    // Load photo as blob using the inspection API (same as InspectionsPage)
    const loadPhoto = useCallback(async (inspectionId, photoType) => {
        const key = `${inspectionId}-${photoType}`;
        if (loadedPhotos[key] || loadingPhotos[key]) return;
        
        setLoadingPhotos(prev => ({ ...prev, [key]: true }));
        try {
            const url = inspectionAPI.getPhotoUrl(inspectionId, photoType);
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('fleet_token')}`
                }
            });
            if (response.ok) {
                const blob = await response.blob();
                const objectUrl = URL.createObjectURL(blob);
                setLoadedPhotos(prev => ({ ...prev, [key]: objectUrl }));
            }
        } catch (error) {
            console.error(`Failed to load ${photoType} photo:`, error);
        } finally {
            setLoadingPhotos(prev => ({ ...prev, [key]: false }));
        }
    }, [loadedPhotos, loadingPhotos]);

    // Load all photos when inspection data is available
    useEffect(() => {
        if (summaryData?.lastInspection?.id) {
            const photos = summaryData?.photos || [];
            const checkoutPhotos = summaryData?.checkoutPhotos || [];
            
            photos.forEach(photo => {
                if (photo.photo_type) {
                    loadPhoto(summaryData.lastInspection.id, photo.photo_type);
                }
            });
            
            checkoutPhotos.forEach(photo => {
                const photoType = photo.type || photo.photo_type;
                if (photoType) {
                    loadPhoto(summaryData.lastInspection.id, photoType);
                }
            });
        }
    }, [summaryData?.lastInspection?.id]);

    // Get loaded photo URL or return loading state
    const getPhotoUrl = (photo) => {
        if (!photo?.inspectionId || !photo?.photoType) return null;
        const key = `${photo.inspectionId}-${photo.photoType}`;
        return loadedPhotos[key] || null;
    };

    const isPhotoLoading = (photo) => {
        if (!photo?.inspectionId || !photo?.photoType) return false;
        const key = `${photo.inspectionId}-${photo.photoType}`;
        return loadingPhotos[key] || false;
    };

    if (!vehicle) return null;

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-2xl bg-white p-0 overflow-hidden max-h-[90vh]">
                <DialogHeader className="p-4 pb-2 border-b border-slate-200 bg-gradient-to-r from-[#1B7EB5]/10 to-transparent">
                    <DialogTitle className="flex items-center gap-3 text-slate-800">
                        <div className="w-10 h-10 bg-[#1B7EB5] rounded-lg flex items-center justify-center">
                            <Car className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <div className="font-bold">{vehicle.make} {vehicle.model}</div>
                            <div className="text-sm font-normal text-slate-500">{vehicle.license_plate}</div>
                        </div>
                    </DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="flex items-center justify-center h-64">
                        <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
                    </div>
                ) : (
                    <ScrollArea className="h-[500px]">
                        <div className="p-4 space-y-4">
                            {/* Photos Section */}
                            {allPhotos.length > 0 && (
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                        <Camera className="w-4 h-4 text-[#1B7EB5]" />
                                        {language === 'el' ? 'Τελευταίες Φωτογραφίες' : 'Latest Photos'}
                                    </h3>
                                    <div className="relative bg-slate-100 rounded-lg overflow-hidden">
                                        <div className="aspect-video relative">
                                            {isPhotoLoading(allPhotos[selectedPhotoIndex]) ? (
                                                <div className="w-full h-full flex items-center justify-center bg-slate-200">
                                                    <Loader2 className="w-8 h-8 animate-spin text-[#1B7EB5]" />
                                                </div>
                                            ) : getPhotoUrl(allPhotos[selectedPhotoIndex]) ? (
                                                <img
                                                    src={getPhotoUrl(allPhotos[selectedPhotoIndex])}
                                                    alt={`Vehicle photo ${selectedPhotoIndex + 1}`}
                                                    className="w-full h-full object-cover"
                                                    onError={(e) => {
                                                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%2394a3b8" font-size="12">No Image</text></svg>';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center bg-slate-200 text-slate-400">
                                                    <span className="text-lg">No Image</span>
                                                </div>
                                            )}
                                            {/* Photo type badge */}
                                            <div className={`absolute top-2 left-2 px-2 py-1 rounded text-xs font-medium ${
                                                allPhotos[selectedPhotoIndex]?.type === 'checkout' 
                                                    ? 'bg-orange-500 text-white' 
                                                    : 'bg-green-500 text-white'
                                            }`}>
                                                {allPhotos[selectedPhotoIndex]?.type === 'checkout' ? 'Check-Out' : 'Check-In'}
                                            </div>
                                            <div className="absolute top-2 right-2 px-2 py-1 bg-black/50 rounded text-xs text-white">
                                                {allPhotos[selectedPhotoIndex]?.photoType || allPhotos[selectedPhotoIndex]?.photo_type}
                                            </div>
                                        </div>
                                        {/* Navigation */}
                                        {allPhotos.length > 1 && (
                                            <>
                                                <button
                                                    onClick={() => setSelectedPhotoIndex(prev => prev > 0 ? prev - 1 : allPhotos.length - 1)}
                                                    className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                                                >
                                                    <ChevronLeft className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => setSelectedPhotoIndex(prev => prev < allPhotos.length - 1 ? prev + 1 : 0)}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white"
                                                >
                                                    <ChevronRight className="w-5 h-5" />
                                                </button>
                                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
                                                    {allPhotos.map((_, idx) => (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setSelectedPhotoIndex(idx)}
                                                            className={`w-2 h-2 rounded-full transition-all ${
                                                                idx === selectedPhotoIndex ? 'bg-white w-4' : 'bg-white/50'
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Info Grid */}
                            <div className="grid grid-cols-2 gap-3">
                                {/* Fuel Level */}
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                        <Fuel className="w-4 h-4" />
                                        {language === 'el' ? 'Στάθμη Καυσίμου' : 'Fuel Level'}
                                    </div>
                                    {summaryData?.fuelLevel ? (
                                        <div className="space-y-1">
                                            <div className={`text-lg font-bold ${fuelLevelConfig[summaryData.fuelLevel]?.color}`}>
                                                {fuelLevelConfig[summaryData.fuelLevel]?.label}
                                            </div>
                                            <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
                                                <div 
                                                    className={`h-full ${summaryData.fuelLevel === 'empty' ? 'bg-red-500' : summaryData.fuelLevel === 'quarter' ? 'bg-orange-500' : summaryData.fuelLevel === 'half' ? 'bg-yellow-500' : 'bg-green-500'}`}
                                                    style={{ width: `${fuelLevelConfig[summaryData.fuelLevel]?.percent}%` }}
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-slate-400 text-sm">
                                            {language === 'el' ? 'Δεν υπάρχουν δεδομένα' : 'No data'}
                                        </div>
                                    )}
                                </div>

                                {/* Last Driver */}
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                        <User className="w-4 h-4" />
                                        {language === 'el' ? 'Τελευταίος Οδηγός' : 'Last Driver'}
                                    </div>
                                    {summaryData?.lastDriver ? (
                                        <div>
                                            <div className="font-medium text-slate-800">{summaryData.lastDriver.name}</div>
                                            <div className="text-xs text-slate-500">{summaryData.lastDriver.email}</div>
                                        </div>
                                    ) : (
                                        <div className="text-slate-400 text-sm">
                                            {language === 'el' ? 'Κανένας οδηγός' : 'No driver'}
                                        </div>
                                    )}
                                </div>

                                {/* Last Inspection */}
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                        <Clock className="w-4 h-4" />
                                        {language === 'el' ? 'Τελευταίος Έλεγχος' : 'Last Inspection'}
                                    </div>
                                    {summaryData?.lastInspection ? (
                                        <div>
                                            <div className="font-medium text-slate-800">
                                                {formatDate(summaryData.lastInspection.created_at)}
                                            </div>
                                            <div className="text-xs text-slate-500 flex items-center gap-1">
                                                {summaryData.lastInspection.checkout_completed ? (
                                                    <><CheckCircle className="w-3 h-3 text-green-500" /> {language === 'el' ? 'Ολοκληρωμένο' : 'Completed'}</>
                                                ) : (
                                                    <><Clock className="w-3 h-3 text-orange-500" /> {language === 'el' ? 'Σε εξέλιξη' : 'In progress'}</>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-slate-400 text-sm">
                                            {language === 'el' ? 'Κανένας έλεγχος' : 'No inspection'}
                                        </div>
                                    )}
                                </div>

                                {/* Mileage */}
                                <div className="p-3 bg-slate-50 rounded-lg">
                                    <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                                        <MapPin className="w-4 h-4" />
                                        {language === 'el' ? 'Χιλιόμετρα' : 'Mileage'}
                                    </div>
                                    <div className="font-medium text-slate-800">
                                        {vehicle.current_mileage?.toLocaleString() || 0} km
                                    </div>
                                </div>
                            </div>

                            {/* Damages Section */}
                            {summaryData?.damages?.length > 0 && (
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold text-red-600 flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        {language === 'el' ? 'Αναφορές Ζημιών' : 'Damage Reports'}
                                        <span className="ml-auto text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                                            {summaryData.damages.length}
                                        </span>
                                    </h3>
                                    <div className="space-y-2">
                                        {summaryData.damages.map((damage, idx) => (
                                            <div key={idx} className={`p-2 rounded-lg border ${
                                                damage.severity === 'high' ? 'bg-red-50 border-red-200' :
                                                damage.severity === 'medium' ? 'bg-orange-50 border-orange-200' :
                                                'bg-yellow-50 border-yellow-200'
                                            }`}>
                                                <div className="flex items-start gap-2">
                                                    <span className="text-xs">
                                                        {damage.severity === 'high' ? '🔴' : damage.severity === 'medium' ? '🟠' : '🟡'}
                                                    </span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm text-slate-700">
                                                            {language === 'el' ? damage.damage_description_el : damage.damage_description_en}
                                                        </p>
                                                        <p className="text-xs text-slate-500 mt-1">
                                                            {formatDate(damage.created_at)} • {damage.photo_type}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Upcoming Maintenance */}
                            {summaryData?.maintenanceRecords?.length > 0 && (
                                <div className="space-y-2">
                                    <h3 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                                        <Wrench className="w-4 h-4 text-[#1B7EB5]" />
                                        {language === 'el' ? 'Συντήρηση' : 'Maintenance'}
                                    </h3>
                                    <div className="space-y-2">
                                        {summaryData.maintenanceRecords.slice(0, 3).map((record, idx) => (
                                            <div key={idx} className="p-2 bg-slate-50 rounded-lg flex items-center justify-between">
                                                <div>
                                                    <div className="text-sm font-medium text-slate-700">{record.maintenance_type}</div>
                                                    <div className="text-xs text-slate-500">
                                                        {record.scheduled_date ? formatDate(record.scheduled_date) : '-'}
                                                    </div>
                                                </div>
                                                <span className={`text-xs px-2 py-0.5 rounded ${
                                                    record.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                    record.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                                    'bg-yellow-100 text-yellow-700'
                                                }`}>
                                                    {record.status}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* No Data Message */}
                            {!summaryData?.lastInspection && !summaryData?.damages?.length && (
                                <div className="text-center py-8 text-slate-400">
                                    <Car className="w-12 h-12 mx-auto mb-2 opacity-50" />
                                    <p>{language === 'el' ? 'Δεν υπάρχουν δεδομένα ακόμα' : 'No data available yet'}</p>
                                </div>
                            )}
                        </div>
                    </ScrollArea>
                )}

                {/* Footer */}
                <div className="p-3 border-t border-slate-200 bg-slate-50 flex justify-between items-center">
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.href = `/app/vehicles/${vehicle.id}`}
                            className="text-[#1B7EB5]"
                        >
                            <ExternalLink className="w-4 h-4 mr-1" />
                            {language === 'el' ? 'Λεπτομέρειες' : 'Details'}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.location.href = `/app/comparison?vehicle_id=${vehicle.id}`}
                            className="text-slate-600"
                        >
                            <Camera className="w-4 h-4 mr-1" />
                            {language === 'el' ? 'Σύγκριση' : 'Compare'}
                        </Button>
                    </div>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onClose}
                    >
                        {language === 'el' ? 'Κλείσιμο' : 'Close'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
